import { useState, useRef, useCallback } from 'react';
import { validateAddress, toStateName } from '../api/addressValidation';

// Default field mapping: form field name → canonical API field name
const DEFAULT_FIELD_MAP = {
  street: 'address1',
  street2: 'address2',
  city: 'city',
  state: 'state',
  zip: 'zip',
};

/**
 * Custom hook for USPS address validation with dialog management.
 *
 * @param {Object} options
 * @param {Object} options.fieldMap - Maps canonical names (street, street2, city, state, zip)
 *   to your form's field names. Example: { street: 'address_line1', zip: 'zipCode' }
 * @param {string} options.countryField - Form field name for country. If set and value is
 *   not 'US'/'USA', validation is skipped.
 *
 * Usage:
 *   const { validateAndProceed, dialogState, ... } = useAddressValidation({
 *     fieldMap: { street: 'address1', zip: 'zipCode' }
 *   });
 *
 *   // In your submit handler:
 *   validateAndProceed(formData, (finalData) => { saveToDB(finalData); });
 */
const useAddressValidation = ({ fieldMap = {}, countryField } = {}) => {
  const [isValidating, setIsValidating] = useState(false);
  const [dialogState, setDialogState] = useState({
    type: null, // 'suggest' | 'warning' | null
    originalAddress: null,
    suggestedAddress: null,
  });

  // Store pending callback and form data across dialog interactions
  const pendingCallbackRef = useRef(null);
  const pendingFormDataRef = useRef(null);

  // Merge default + custom field map
  const mergedMap = { ...DEFAULT_FIELD_MAP, ...fieldMap };

  // Extract address fields from form data using the field map
  const extractAddress = useCallback((formData) => {
    return {
      street: formData[mergedMap.street] || '',
      street2: formData[mergedMap.street2] || '',
      city: formData[mergedMap.city] || '',
      state: formData[mergedMap.state] || '',
      zip: formData[mergedMap.zip] || '',
    };
  }, [mergedMap.street, mergedMap.street2, mergedMap.city, mergedMap.state, mergedMap.zip]);

  // Merge standardized address back into form data using the field map
  // Converts USPS 2-letter state code back to full name for dropdown compatibility
  const mergeAddress = useCallback((formData, standardized) => {
    return {
      ...formData,
      [mergedMap.street]: standardized.street || '',
      [mergedMap.street2]: standardized.street2 || '',
      [mergedMap.city]: standardized.city || '',
      [mergedMap.state]: toStateName(standardized.state) || '',
      [mergedMap.zip]: standardized.zip || '',
    };
  }, [mergedMap.street, mergedMap.street2, mergedMap.city, mergedMap.state, mergedMap.zip]);

  // Check if address fields have enough data to validate (street + zip minimum)
  const hasAddressData = useCallback((formData) => {
    const addr = extractAddress(formData);
    return addr.street && addr.zip;
  }, [extractAddress]);

  /**
   * Main entry point: validate address, then proceed or show dialog.
   * @param {Object} formData - Full form data object
   * @param {Function} onProceed - Callback with final form data when user confirms
   */
  const validateAndProceed = useCallback(async (formData, onProceed) => {
    // Skip validation if no address data or non-US country
    if (!hasAddressData(formData)) {
      onProceed(formData);
      return;
    }

    if (countryField && formData[countryField]) {
      const country = formData[countryField].toUpperCase();
      if (country !== 'US' && country !== 'USA' && country !== 'UNITED STATES') {
        onProceed(formData);
        return;
      }
    }

    pendingCallbackRef.current = onProceed;
    pendingFormDataRef.current = formData;

    setIsValidating(true);
    try {
      const address = extractAddress(formData);
      console.log('Address validation payload:', address);
      const result = await validateAddress(address);

      if (result.valid && !result.modified) {
        // Address matched exactly — proceed immediately
        setDialogState({ type: null, originalAddress: null, suggestedAddress: null });
        onProceed(formData);
      } else if (result.valid && result.modified) {
        // Address corrected — show suggestion dialog
        setDialogState({
          type: 'suggest',
          originalAddress: address,
          suggestedAddress: result.standardized,
        });
      } else {
        // Address invalid — show warning dialog
        setDialogState({
          type: 'warning',
          originalAddress: address,
          suggestedAddress: null,
        });
      }
    } catch (error) {
      const status = error?.response?.status;
      const responseData = error?.response?.data;

      // If API returns a validation result (e.g., 400 with valid: false), show warning dialog
      if (status === 400 || (responseData && responseData.valid === false)) {
        setDialogState({
          type: 'warning',
          originalAddress: extractAddress(formData),
          suggestedAddress: null,
        });
      } else {
        // Network/server error — let user proceed without validation
        console.warn('Address validation unavailable:', status || error.message);
        onProceed(formData);
      }
    } finally {
      setIsValidating(false);
    }
  }, [extractAddress, hasAddressData, countryField]);

  // User accepts the USPS-suggested address
  const acceptSuggestion = useCallback(() => {
    const formData = pendingFormDataRef.current;
    const onProceed = pendingCallbackRef.current;
    const suggested = dialogState.suggestedAddress;

    if (formData && onProceed && suggested) {
      const mergedData = mergeAddress(formData, suggested);
      setDialogState({ type: null, originalAddress: null, suggestedAddress: null });
      onProceed(mergedData);
    }
  }, [dialogState.suggestedAddress, mergeAddress]);

  // User keeps their original address (from suggestion dialog)
  const keepOriginal = useCallback(() => {
    const formData = pendingFormDataRef.current;
    const onProceed = pendingCallbackRef.current;

    setDialogState({ type: null, originalAddress: null, suggestedAddress: null });
    if (formData && onProceed) {
      onProceed(formData);
    }
  }, []);

  // User saves anyway despite invalid address
  const saveAnyway = useCallback(() => {
    const formData = pendingFormDataRef.current;
    const onProceed = pendingCallbackRef.current;

    setDialogState({ type: null, originalAddress: null, suggestedAddress: null });
    if (formData && onProceed) {
      onProceed(formData);
    }
  }, []);

  // User cancels — dismiss dialog, don't proceed
  const cancelDialog = useCallback(() => {
    setDialogState({ type: null, originalAddress: null, suggestedAddress: null });
    pendingCallbackRef.current = null;
    pendingFormDataRef.current = null;
  }, []);

  return {
    validateAndProceed,
    isValidating,
    dialogState,
    acceptSuggestion,
    keepOriginal,
    saveAnyway,
    cancelDialog,
  };
};

export default useAddressValidation;
